import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { fetchKlipyGifs } from "./klipyApi";
import "./gif-picker.styles.scss";

const RESULT_LIMIT = 18;
const SEARCH_DELAY_MS = 350;
const DEFAULT_ATTRIBUTION_LOGO = "/images/klipy/powered-by-klipy.svg";

export default function GifPicker({
  apiKey,
  onSelect,
  attributionLogoSrc = DEFAULT_ATTRIBUTION_LOGO,
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logoFailed, setLogoFailed] = useState(false);

  const requestRef = useRef(0);
  const abortRef = useRef(null);

  const load = useCallback(
    async (nextQuery) => {
      const requestId = ++requestRef.current;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError("");

      try {
        const result = await fetchKlipyGifs({
          apiKey,
          query: nextQuery,
          limit: RESULT_LIMIT,
          cursor: "",
          signal: controller.signal,
        });

        if (requestId !== requestRef.current) return;
        setItems(result.items.slice(0, RESULT_LIMIT));
      } catch (requestError) {
        if (requestError?.name === "AbortError") return;
        if (requestId !== requestRef.current) return;

        setError(requestError?.message || "Could not load GIFs from KLIPY.");
        setItems([]);
      } finally {
        if (requestId !== requestRef.current) return;
        setLoading(false);

        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [apiKey],
  );

  useEffect(() => {
    const cleanQuery = query.trim();
    const timer = setTimeout(
      () => load(cleanQuery),
      cleanQuery ? SEARCH_DELAY_MS : 0,
    );

    return () => clearTimeout(timer);
  }, [query, load]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <div className="gifPicker gifPicker--embedded" aria-label="GIF picker">
      <div className="gifPicker__searchRow">
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search KLIPY"
          aria-label="Search KLIPY"
        />

        {query && (
          <button
            type="button"
            className="gifPicker__clear"
            onClick={() => setQuery("")}
            aria-label="Clear GIF search"
          >
            <HiXMark />
          </button>
        )}
      </div>

      <div className="gifPicker__sectionLabel">
        {query.trim() ? `Results for “${query.trim()}”` : "Featured"}
      </div>

      <div className="gifPicker__content">
        {loading ? (
          <div className="gifPicker__status">
            <span className="gifPicker__spinner" />
            <span>Loading GIFs...</span>
          </div>
        ) : error ? (
          <div className="gifPicker__status gifPicker__status-error">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="gifPicker__status">No GIFs found.</div>
        ) : (
          <div className="gifPicker__grid">
            {items.map((gif) => (
              <button
                type="button"
                key={`${gif.id}:${gif.previewUrl}`}
                className="gifPicker__tile"
                onClick={() => onSelect?.(gif)}
                title={gif.title || "Send GIF"}
              >
                <img
                  src={gif.previewUrl || gif.url}
                  alt={gif.title || "GIF"}
                  loading="lazy"
                />

                {(gif.creator || gif.contentSource) && (
                  <span className="gifPicker__creator">
                    {gif.creator
                      ? `@${String(gif.creator).replace(/^@/, "")}`
                      : gif.contentSource}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="gifPicker__attribution">
        {!logoFailed && attributionLogoSrc ? (
          <img
            src={attributionLogoSrc}
            alt="Powered by KLIPY"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span>Powered by KLIPY</span>
        )}
      </div>
    </div>
  );
}
